import { useQuery } from '@apollo/client';
import type { Pills } from '@seek/chalice-types';

import {
  GetJobSearchAssistDocument,
  type JobSearchAssistPills,
} from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';
import { useFeatureFlagRefineBarV2Experiment } from 'src/store/featureFlags/hooks';
import { useDispatch, useSelector } from 'src/store/react';
import { updateBaseKeywordParam, updateKeywordsField } from 'src/store/search';
import { selectBaseKeywords } from 'src/store/search/selectors';
import { useRunSearch } from 'src/store/search/useRunSearch';
import {
  selectFeatureFlag,
  selectPills,
  selectIsResultsLoading,
  selectAsyncPillsToken,
  selectSolId,
} from 'src/store/selectors';

export type JobSearchAssist = {
  isLoading: boolean;
  pills: Pills | JobSearchAssistPills['data'];
  onPillClick: (keyword: string) => void;
};

export const useJobSearchAssist = (): JobSearchAssist => {
  const dispatch = useDispatch();
  const _isJobSearchAssistEnabled = useSelector(
    selectFeatureFlag('isJobSearchAssistEnabled'),
  );
  const runSearch = useRunSearch();
  const baseKeywords = useSelector(selectBaseKeywords);
  const visitorId = useSelector(selectSolId);
  const pills = useSelector(selectPills);
  const isLoading = useSelector(selectIsResultsLoading);
  const asyncPillsToken = useSelector(selectAsyncPillsToken);
  const tokens = [asyncPillsToken];

  const { refineBarV2, dynamicPillsV2 } = useFeatureFlagRefineBarV2Experiment();
  const isJobSearchAssistEnabled =
    _isJobSearchAssistEnabled &&
    asyncPillsToken &&
    (refineBarV2 || dynamicPillsV2);

  const { loading: isJobSearchAssistLoading, data } = useQuery(
    GetJobSearchAssistDocument,
    {
      variables: {
        tokens,
        visitorId,
      },
      context: {
        headers: {
          'x-custom-features': 'application/features.seek.all+json',
        },
      },
      fetchPolicy: 'cache-and-network',
      skip: !isJobSearchAssistEnabled,
      ssr: false,
      onError: (err) => {
        logger.error(
          { err, input: { tokens } },
          'JobSearchAssist GraphQL Failure',
        );
      },
    },
  );

  const jobSearchAssistPills = data?.jobSearchAssist?.pills?.data || [];

  return isJobSearchAssistEnabled
    ? {
        isLoading: isJobSearchAssistLoading || isLoading,
        pills: jobSearchAssistPills,
        onPillClick: (keyword: string) => {
          dispatch(updateKeywordsField(keyword));
          dispatch(runSearch({ pathname: '/jobs' }));
        },
      }
    : {
        isLoading,
        pills: pills || [],
        onPillClick: (keyword: string) => {
          if (!baseKeywords) {
            dispatch(updateBaseKeywordParam(keyword));
          }
          dispatch(updateKeywordsField(keyword));
          dispatch(runSearch({ pathname: '/jobs' }));
        },
      };
};

export default useJobSearchAssist;
